/* eslint-disable @typescript-eslint/no-unused-vars */
import { Group } from "@iventis/types";
import { DataField } from "@iventis/domain-model/model/dataField";
import { DataFieldType } from "@iventis/domain-model/model/dataFieldType";
import { EventBlock } from "@iventis/domain-model/model/eventBlock";
import { findNode } from "@iventis/tree-browser/tree-operations";
import {
    differenceInCalendarDays,
    startOfDay,
    addDays,
    format,
    add,
    compareAsc,
    isBefore,
    addMinutes,
    isAfter,
    subMinutes,
    addHours,
    addSeconds,
    differenceInSeconds,
} from "date-fns";
import * as Yup from "yup";
import { Content } from "@iventis/translations/content/typed-content";
import { NodeType } from "@iventis/domain-model/model/nodeType";
import { Status } from "@iventis/domain-model/model/status";
import { Authorisation } from "@iventis/domain-model/model/authorisation";
import { Hierarchy } from "@iventis/domain-model/model/hierarchy";
import { ScheduleConfig } from "@iventis/domain-model/model/scheduleConfig";
import { DataFieldValue } from "@iventis/domain-model/model/dataFieldValue";
import { DataFieldListItem } from "@iventis/domain-model/model/dataFieldListItem";
import { ScheduleVersion } from "@iventis/domain-model/model/scheduleVersion";
import { ScheduleStatus } from "@iventis/domain-model/model/scheduleStatus";
import { VenueLocation } from "@iventis/domain-model/model/venueLocation";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import { EventBlockTypeDataFields } from "@iventis/domain-model/model/eventBlockTypeDataFields";
import { EventBlockSystemDataFieldType } from "@iventis/domain-model/model/eventBlockSystemDataFieldType";
import { EntityWithDataFieldValues } from "@iventis/domain-model/model/entityWithDataFieldValues";
import { UseIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { toast } from "@iventis/toasts/src/toast";
import { ScheduleConfigVenueLocationType } from "@iventis/domain-model/model/scheduleConfigVenueLocationType";
import { translate } from "@iventis/translations/translation";
import { convertTimeStringToNum, EMPTY_GUID, formatDateInTimezone } from "@iventis/utilities";

import { SportEventDataFieldNames } from "./datafields";
import { ScheduleVersionNode, SummaryRowGroup } from "./schedule.types";
import { getVenueCodeAndName } from "./sport-event-helpers";
import { TimeStrings } from "./event.types";
import { findEventBlockHierarchyEntitiesByCalendarLevel, findHierarchyEntityById, loopHierarchy } from "./hierarchy.helpers";
import { getEventBlockTypeByCalendarLevel } from "./event-block-helpers";
import { FilterType } from "./filter-rules";

/**
 * Generates a group id for events specifically not tied to a group
 */
export const generateUndefinedGroupId = (groupId: string) => `undefined-${groupId}`;

export const findScheduleNode = (arr: ScheduleVersionNode[], nodeId: string): ScheduleVersionNode => findNode(arr, nodeId) as ScheduleVersionNode;

/**
 * Sorts a given group hierarchy alphabetically
 */
export const sortGroups = (groups: Group[], compareProperty: "groupName" | "groupCode" = "groupName") => {
    const sort = (_groups: Group[]) => _groups.sort((a, b) => a[compareProperty]?.localeCompare(b[compareProperty]));
    groups.forEach((g) => {
        if (g.subGroups) {
            // eslint-disable-next-line no-param-reassign
            g.subGroups = sortGroups(g.subGroups);
        }
    });
    return sort(groups);
};

// Get's an event block represented on a single day, for use where we view days side by side in the calendar. Returns in UTC; NOT a timezoned value.
export const getEventRangeClampedToDate = (event: EventBlock, date: Date, timezone: string) => {
    const dates = {
        start: undefined,
        end: undefined,
    };

    // Please do not mess with this logic. The reasoning for the conversion to a zone and then out of a zone has been lost to the sands of time.
    // If you remove the conversion, it will not work anymore.
    const stuffingDayZoned = utcToZonedTime(date, timezone);
    const eventStartZoned = utcToZonedTime(event.startAt, timezone);
    const eventEndZoned = utcToZonedTime(event.endAt, timezone);

    if (event.startAt) {
        dates.start = stuffingDayZoned;
        dates.start = addHours(dates.start, eventStartZoned.getHours());
        dates.start = addMinutes(dates.start, eventStartZoned.getMinutes());
        dates.start = addSeconds(dates.start, eventStartZoned.getSeconds());
    }

    if (event.endAt) {
        dates.end = stuffingDayZoned;
        dates.end = addHours(dates.end, eventEndZoned.getHours());
        dates.end = addMinutes(dates.end, eventEndZoned.getMinutes());
        dates.end = addSeconds(dates.end, eventEndZoned.getSeconds());
    }

    if (event.endAt < event.startAt) {
        addDays(event.endAt, 1);
    }

    const startUnzoned = zonedTimeToUtc(dates.start, timezone);
    const endUnzoned = zonedTimeToUtc(dates.end, timezone);

    return { start: startUnzoned, end: endUnzoned };
};

export const groupsFromDays = (days: Map<number, Date>, timezone: string): Group[] =>
    Array.from(days).map(
        ([dayNumber, date]) =>
            ({
                groupId: dayNumber.toString(),
                groupName: dayNumber.toString(),
                subGroups: [{ groupId: `${dayNumber.toString()}_date`, groupName: format(utcToZonedTime(date, timezone), "EEE d MMM"), subGroups: undefined }],
            } as Group)
    );

// Determines a day number from a list of event days, given a specific date
export const dayNumberFromDate = (days: Map<number, Date>, lookingForDate: Date): number => {
    // Get all the days and the number of seconds offset they are from the day we are looking for
    const allDays = Array.from(days.entries()).map(([day, date]) => ({
        day,
        date,
        secondsOffset: differenceInSeconds(date, lookingForDate),
    }));
    // 86,400 seconds in a day, so  look for a day with an offset <= 0 and > -86,400
    const day = allDays.find((d) => d.secondsOffset <= 0 && d.secondsOffset > -86400);
    if (!day) {
        throw new Error(`Tried to look for a day number for ${lookingForDate} which does not exist.`);
    }
    return day.day;
};

export const mapHierarchyToGroups = (groupsData: Hierarchy<VenueLocation>[]): Group[] =>
    groupsData.map((groupData) => {
        const singleGroup: Group = {
            groupId: groupData.item.id,
            groupName: groupData.item.name,
            subGroups: groupData.children != null && groupData.children.length > 0 ? mapHierarchyToGroups(groupData.children) : undefined,
        };
        return singleGroup;
    });

export const getScheduleVersionDataFieldValues = (
    scheduleVersionHierarchy: Hierarchy<ScheduleVersion>,
    dataFieldName: SportEventDataFieldNames
): { [scheduleVersionId: string]: DataFieldValue["value"] } =>
    scheduleVersionHierarchy.children.reduce(
        (aggregated, current) => ({ ...aggregated, [current.item.id]: current.item.dataFieldValues.find(({ dataField }) => dataField.name === dataFieldName)?.value }),
        {} as { [scheduleVersionId: string]: DataFieldValue["value"] }
    );

/**
 * Constructs an object pairing a venue ID with an array of schedule version IDs
 */
export const pairVenueIdWithScheduleVersionIdFromSessions = (sessions: Hierarchy<EventBlock>[]) => {
    const venueIdScheduleVersionIds: { [venueId: string]: string[] } = {};
    sessions.forEach((session) => {
        const sessionVenueId = (session.item as EventBlock).venueLocation.id;
        const sessionScheduleVersionId = (session.item as EventBlock).scheduleVersionId;

        if (!venueIdScheduleVersionIds[sessionVenueId]) {
            venueIdScheduleVersionIds[sessionVenueId] = [sessionScheduleVersionId];
        } else if (!venueIdScheduleVersionIds[sessionVenueId].find((scheduleVersionId) => scheduleVersionId === sessionScheduleVersionId)) {
            venueIdScheduleVersionIds[sessionVenueId].push(sessionScheduleVersionId);
        }
    });

    return venueIdScheduleVersionIds;
};

/**
 * Completes the calendar groups hierarchy by filling in non-existent groups with an empty group
 */
export const fillEmptyGroups = (groups: Group[], showBottomLevel: boolean): Group[] => {
    const copyGroups = JSON.parse(JSON.stringify(groups)) as Group[];
    copyGroups.forEach((group, parentIndex) => {
        if (group.subGroups == null || group.subGroups.length === 0) {
            copyGroups[parentIndex].subGroups = [{ groupId: `empty-sub-${group.groupId}`, groupName: "", subGroups: showBottomLevel ? [] : undefined }];
        }
        group.subGroups.forEach((group, index) => {
            if (showBottomLevel && (group.subGroups == null || group.subGroups.length === 0)) {
                copyGroups[parentIndex].subGroups[index].subGroups = [{ groupId: `empty-sub-sub-${group.groupId}`, groupName: "", subGroups: undefined }];
            }
        });
    });
    return copyGroups;
};

/**
 * Generates an id based on the group id and its top level groupId
 */
export const venueSubGroupIdGenerator = (venueId: string, topLevelId: string) => venueId + topLevelId;

/**
 * Group by Venue
 */
export const groupByVenue = (
    venueLocations: Hierarchy<VenueLocation>[],
    eventBlockHierarchy: Hierarchy<EventBlock>[],
    showEventUnits: boolean,
    translatedText: GetVenueGroupsTranslatedText,
    venueLocationTypes: ScheduleConfigVenueLocationType[]
): Group[] => {
    const allSubGroupsContainingEvents = getVenueGroupsFromEventBlockHierarchy(eventBlockHierarchy, showEventUnits, translatedText);
    if (includeThirdLevelVenue(venueLocationTypes)) {
        return venueLocations.reduce((filteredVenues: Group[], venue: Hierarchy<VenueLocation>) => {
            const subGroups = allSubGroupsContainingEvents.filter((g) => findHierarchyEntityById<VenueLocation>(venueLocations, g.groupId).item.parentId === venue.item.id);
            if (subGroups.length > 0) {
                filteredVenues.push({
                    groupId: venue.item.id,
                    groupName: venue.item.name,
                    subGroups,
                });
            }
            return filteredVenues;
        }, []);
    }
    return allSubGroupsContainingEvents;
};

/**
 * Group calendar by day with venue and spaces as sub groups
 */
export const groupByDay = (
    eventBlockHierarchy: Hierarchy<EventBlock>,
    days: Map<number, Date>,
    showEventUnits: boolean,
    translatedText: GetVenueGroupsTranslatedText,
    timezone: string
): Group[] => {
    const daysToEventBlocks: Record<number, Hierarchy<EventBlock>[]> = {};

    eventBlockHierarchy.children.forEach((session) => {
        const day = dayNumberFromDate(days, new Date(session.item.startAt));
        if (day != null) {
            if (Object.keys(daysToEventBlocks).some((_day) => _day === day.toString())) {
                daysToEventBlocks[day].push(session);
            } else {
                daysToEventBlocks[day] = [session];
            }
        }
    });

    return Array.from(days).map(([day, date]) => ({
        groupId: day.toString(),
        groupName: `${day} (${format(utcToZonedTime(date, timezone), "EEE d MMM")})`,
        subGroups: sortGroups(getVenueGroupsFromEventBlockHierarchy(daysToEventBlocks[day] || [], showEventUnits, translatedText, day.toString())),
    }));
};

/**
 * Group calendar by a given data field with venues and spaces as sub groups
 */
export const groupByDataField = (
    dataFieldId: string,
    selectedDataFieldListItems: DataFieldListItem[],
    eventBlockHierarchy: Hierarchy<EventBlock>,
    selectedScheduleVersionHierarchy: Hierarchy<ScheduleVersion>,
    showEventUnits: boolean,
    translatedText: GetVenueGroupsTranslatedText
): Group[] => {
    if (selectedScheduleVersionHierarchy.children.length === 0 || eventBlockHierarchy.children.length === 0) {
        // With no event blocks, we have no access to any data field list values to display as groups
        return [
            {
                groupId: "1",
                groupName: translatedText.noSessionsInView,
                subGroups: [],
            },
        ];
    }
    // Create an array of the data field list items which actually contain sessions and attach those sessions to the list item
    const dataFieldListItemsContainingSessions: { listItem: DataFieldListItem; attachedEventBlocks: Hierarchy<EventBlock>[] }[] = [];

    eventBlockHierarchy.children.forEach((session) => {
        const dfValue = findInheritedDataFieldValue(session, dataFieldId, eventBlockHierarchy, selectedScheduleVersionHierarchy);

        // If the data field value is found AND is contained in the selected data field list items then append/update
        if (dfValue != null && selectedDataFieldListItems.some((dl) => dl.id === dfValue.id)) {
            if (dataFieldListItemsContainingSessions.some((dfv) => dfValue.id === dfv.listItem.id)) {
                const index = dataFieldListItemsContainingSessions.findIndex((dfv) => dfv.listItem.id === dfValue.id);
                dataFieldListItemsContainingSessions[index].attachedEventBlocks.push(session);
            } else {
                dataFieldListItemsContainingSessions.push({ listItem: dfValue, attachedEventBlocks: [session] });
            }
        }
    });

    return dataFieldListItemsContainingSessions.map(({ listItem, attachedEventBlocks }) => ({
        groupId: listItem.id,
        groupName: listItem.name,
        subGroups: getVenueGroupsFromEventBlockHierarchy(attachedEventBlocks, showEventUnits, translatedText, listItem.id),
    }));
};

export type GetVenueGroupsTranslatedText = { noSpaceDefined: string; noSessionsInView: string };

/**
 * Converts event block hierarchy into group hierarchy using the events' venues
 * @param eventBlocks Event block hierarchy
 * @param topLevelId If the return value is used as a sub group, pass the parent group id to ensure each has a unique groupId
 */
const getVenueGroupsFromEventBlockHierarchy = (
    eventBlocks: Hierarchy<EventBlock>[],
    showEventUnits: boolean,
    translatedText: GetVenueGroupsTranslatedText,
    topLevelId = ""
): Group[] => {
    const groups: Group[] = [];
    findEventBlockHierarchyEntitiesByCalendarLevel(eventBlocks, 1).forEach((session) => {
        const groupId = venueSubGroupIdGenerator(session.item.venueLocation.id, topLevelId);
        const subGroups = [];
        findEventBlockHierarchyEntitiesByCalendarLevel(session.children, 2).forEach((subSession) => {
            const subGroupId = venueSubGroupIdGenerator(subSession.item.venueLocation?.id || generateUndefinedGroupId(session.item.venueLocation.id), topLevelId);
            // Prevent repeated sub groups
            if (!subGroups.some((item) => item.groupId === subGroupId)) {
                subGroups.push({
                    groupId: subGroupId,
                    groupName: subSession.item.venueLocation?.name || translatedText.noSpaceDefined,
                    subGroups: undefined,
                });
            }
        });
        // Prevent repeated groups
        if (!groups.some((item) => item.groupId === groupId)) {
            groups.push({
                groupId, // In case venue id is not unique (multi-disciplinary venues)
                groupName: session.item.venueLocation.name,
                subGroups: showEventUnits ? subGroups : undefined,
            });
        } else {
            const index = groups.findIndex((group) => group.groupId === groupId);
            // While in event unit view, if this session's group already exists in groups, we can append any event Units groups that don't already exist into that groups' subGroups array
            if (index !== -1 && showEventUnits) {
                const existingsubGroups = groups[index].subGroups;
                subGroups.forEach((subGroup) => {
                    if (!existingsubGroups.some((sg) => sg.groupId === subGroup.groupId)) {
                        existingsubGroups.push(subGroup);
                    }
                });
            }
        }
    });
    return groups;
};

/**
 * Produces an array of nested groups for the summary table's Y-axis
 * @param locations Venue hierarchy
 * @param venueIdWithScheduleVersionIds Object pairing each venue Id to an array of schedule version IDs
 * @param scheduleVersionsWithDisciplines Object pairing each schedule version ID with a data field value
 * @returns Nested groups as cluster -> Venue -> Discipline
 */
export const hierarchyToSummaryGroups = (
    locations: Hierarchy<VenueLocation>[],
    venueIdWithScheduleVersionIds: { [venueId: string]: string[] },
    scheduleVersionsWithDisciplines: { [scheduleVersionId: string]: DataFieldValue["value"] },
    venueLocationTypes: ScheduleConfigVenueLocationType[]
): SummaryRowGroup[] => {
    const nestedGroups = (venue: Hierarchy<VenueLocation>) => {
        const scheduleVersionIds = venueIdWithScheduleVersionIds[venue.item.id];
        return {
            /* Venue level */
            groupId: venue.item.id,
            groupName: venue.item.name,
            groupCode: venue.item.dataFieldValues.find(({ dataField }) => dataField.name === SportEventDataFieldNames.VENUE_CODE)?.value,
            subGroups: scheduleVersionIds
                ? scheduleVersionIds
                      /* Discipline level */
                      .map((scheduleVersionId) => scheduleVersionsWithDisciplines[scheduleVersionId])
                      // Aggregate schedule versions by discipline
                      .reduce(
                          (cum, disciplineDataFieldValue) =>
                              cum.some((cumDisciplineDataFieldValue) => cumDisciplineDataFieldValue.id === disciplineDataFieldValue.id) ? cum : [...cum, disciplineDataFieldValue],
                          []
                      )
                      .map((disciplineDataFieldValue) => ({
                          groupId: venue.item.id + disciplineDataFieldValue.id,
                          groupName: disciplineDataFieldValue.name,
                          groupCode: disciplineDataFieldValue.metaData.code,
                          subGroups: [],
                      }))
                : // If the venue is not referenced in the event block hierarchy, there are no schedule version IDs present for this venue.
                  [],
        };
    };
    if (includeThirdLevelVenue(venueLocationTypes)) {
        return locations.map((cluster) => ({
            /* Cluster level */
            groupId: cluster.item.id,
            groupName: cluster.item.name,
            groupCode: cluster.item.name,
            subGroups: cluster.children.map(nestedGroups),
        }));
    }
    return locations.map(nestedGroups);
};

/** If there are 3 levels of venueLocations we can assume we include clusters */
export const includeThirdLevelVenue = (venueLocationTypes: ScheduleConfigVenueLocationType[]): boolean => venueLocationTypes?.length === 3;

export const createDayDateMap = (startAt: Date, endAt: Date, dayZero: Date) => {
    const preZeroDays = differenceInCalendarDays(startAt, dayZero);
    const map = new Map<number, Date>();
    let counter = preZeroDays;
    let date = startAt;
    while (date <= endAt) {
        map.set(counter, date);
        counter += 1;
        date = addDays(date, 1);
    }
    return map;
};

export const createValidationForDataFields = (dataFields: DataField[], eventBlockTypeDataFields: EventBlockTypeDataFields[]) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    eventBlockTypeDataFields.reduce((dataFieldValues: Yup.ObjectSchemaDefinition<any>, eventBlockTypeDataField: EventBlockTypeDataFields) => {
        const dataField = dataFields.find((df) => df.id === eventBlockTypeDataField.dataFieldId);
        switch (dataField.type) {
            case DataFieldType.List: {
                const listValidation = Yup.object().nullable();
                // eslint-disable-next-line no-param-reassign
                dataFieldValues[dataField.id] = eventBlockTypeDataField.required ? listValidation.required("Required") : listValidation;
                break;
            }
            case DataFieldType.Text: {
                const textValidation = Yup.string().nullable();
                // eslint-disable-next-line no-param-reassign
                dataFieldValues[dataField.id] = eventBlockTypeDataField.required
                    ? textValidation.required("Required").typeError("Invalid type of value")
                    : textValidation.typeError("Invalid type of value");
                break;
            }
            case DataFieldType.Number: {
                const numberValidation = Yup.number().nullable();
                // eslint-disable-next-line no-param-reassign
                dataFieldValues[dataField.id] = eventBlockTypeDataField.required
                    ? numberValidation.required("Required").typeError("Digits Required")
                    : numberValidation.typeError("Digits Required");
                break;
            }
            case DataFieldType.Tickbox: {
                const boolValidation = Yup.boolean();
                // eslint-disable-next-line no-param-reassign
                dataFieldValues[dataField.id] = eventBlockTypeDataField.required
                    ? boolValidation.required("Required").typeError("Value Required")
                    : boolValidation.typeError("Value Required");
                break;
            }
            default:
                break;
        }
        return dataFieldValues;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    }, {} as Yup.ObjectSchemaDefinition<any>);

export const renderVenueSelectedText = (venues: Hierarchy<VenueLocation>[], allVenues: Hierarchy<VenueLocation>[], translate: UseIventisTranslate) => {
    switch (venues.length) {
        case allVenues.length:
            return translate(Content.schedule.all_venues);
        case 0:
            return translate(Content.schedule.no_venues);
        case 1:
            return getVenueCodeAndName(venues[0].item);
        default:
            return translate(Content.schedule.x_venues_selected, { count: venues.length });
    }
};

export const renderDataFieldSelectedText = (dataFieldName: string, selectedListItems: DataFieldListItem[], allListItems: DataFieldListItem[], translate: UseIventisTranslate) => {
    switch (selectedListItems.length) {
        case allListItems.length:
            return translate(Content.schedule2.data_fields.multi_selector.all[dataFieldName]);
        case 0:
            return translate(Content.schedule2.data_fields.multi_selector.none[dataFieldName]);
        case 1:
            return `${selectedListItems[0].metaData.code} - ${selectedListItems[0].name}`;
        default:
            // eslint-disable-next-line camelcase
            return translate(Content.schedule2.data_fields.multi_selector.x_selected[dataFieldName as keyof typeof Content.schedule2.data_fields.multi_selector.x_selected], {
                count: selectedListItems.length,
            });
    }
};

export const flattenVenueLocations = (venueLocations: Hierarchy<VenueLocation>[], venueLocationTypes: ScheduleConfigVenueLocationType[]) => {
    if (includeThirdLevelVenue(venueLocationTypes)) {
        return venueLocations.reduce((cum, cluster) => [...cum, ...cluster.children], [] as Hierarchy<VenueLocation>[]);
    }
    return venueLocations;
};

export const createScheduleVersionNode = (id: string, name: string, parentId: string): ScheduleVersionNode => ({
    createdByUserId: EMPTY_GUID,
    createdByUserName: null,
    expanded: false,
    favourite: false,
    id,
    lastUpdatedByUserId: EMPTY_GUID,
    lastUpdatedByUserName: null,
    name,
    parentId,
    sourceId: id,
    status: Status.Active,
    type: NodeType.ScheduleVersion,
    createdAt: new Date(),
    childNodes: [],
    authorisation: Authorisation.Admin,
    lastUpdatedAt: new Date(),
    visible: true,
    editName: false,
    childCount: 0,
});

const setTimeOndate = (date: Date, time: string, timezone: string): Date => {
    if (typeof time !== "string") {
        return date;
    }
    const times = time.split(":");
    if (times.length !== 3) {
        throw new Error("Time must be in the format 'hh:mm:ss'");
    }
    const [startHour, startMin, startSec] = times.map((t) => Number(t));

    // In order to set a time which is a string on a date, we need to set the time in the desired timezone, so it has the correct UTC value
    let dateInTimezone = utcToZonedTime(date, timezone);
    dateInTimezone = addHours(dateInTimezone, startHour);
    dateInTimezone = addMinutes(dateInTimezone, startMin);
    dateInTimezone = addSeconds(dateInTimezone, startSec);
    return zonedTimeToUtc(dateInTimezone, timezone);
};

export interface CalendarRange {
    calendarStartPosition: Date;
    calendarEndPosition: Date;
    day?: number;
}

const getCalendarRange = (date: Date, [dayStartAt, dayEndAt]: [string, string], timezone: string, dayNumber?: number): CalendarRange => {
    const start = setTimeOndate(date, dayStartAt, timezone);
    const end = setTimeOndate(date, dayEndAt, timezone);

    const dayOverlap = compareAsc(start, end) === 1;

    const range: CalendarRange = {
        calendarStartPosition: start,
        calendarEndPosition: dayOverlap ? addDays(end, 1) : end,
    };
    if (dayNumber != null) {
        range.day = dayNumber;
    }
    return range;
};

export const getCalendarRangeByDay = (availableDays: Map<number, Date>, dayNumber: number, [dayStartAt, dayEndAt]: [string, string], timezone: string) => {
    const day = availableDays.get(dayNumber);
    return getCalendarRange(day, [dayStartAt, dayEndAt], timezone, dayNumber);
};

export const getCalendarRangeSingleDayDateless = (availableDays: Map<number, Date>, [dayStartAt, dayEndAt]: [string, string], timezone: string) => {
    // When viewing by venue, only a single day exists along the Y-axis. We will use the first event day arbitrarily.
    const firstDay = Array.from(availableDays.values())[0];
    return getCalendarRange(firstDay, [dayStartAt, dayEndAt], timezone);
};

export const getCalendarRangeBasedOnFilterType = (filterType: FilterType, availableDays: Map<number, Date>, start: string, end: string, day: number, timezone: string) =>
    isFilteredByDay(filterType) ? getCalendarRangeByDay(availableDays, day, [start, end], timezone) : getCalendarRangeSingleDayDateless(availableDays, [start, end], timezone);

const getTime = (date) => format(new Date(date), "HH:mm:ss");

export const getStartAndEndAtStringDigits = (eventBlock: EventBlock, timezone: string): TimeStrings => {
    const startAt = formatDateInTimezone(eventBlock.startAt, timezone);
    const endAt = formatDateInTimezone(eventBlock.endAt, timezone);
    return {
        startTime: getTime(startAt),
        endTime: getTime(endAt),
    };
};

export const convertDigitStringToDate = (date: Date, hours: string, mins: string, seconds: string) =>
    add(startOfDay(date), { hours: Number(hours), minutes: Number(mins), seconds: Number(seconds) });

export function removeEventBlocksWithScheduleVersion(eventBlockHierarchy: Hierarchy<EventBlock>, presentScheduleVersions: Hierarchy<ScheduleVersion>[]): Hierarchy<EventBlock> {
    const newEventBlocks = eventBlockHierarchy.children.filter(({ item }) => {
        const eventBlock: EventBlock = item as EventBlock;
        return presentScheduleVersions.some(({ item }) => item.id === eventBlock.scheduleVersionId);
    });
    return { ...eventBlockHierarchy, children: [...newEventBlocks] };
}

export function getSpecificDataFields(scheduleConfig: ScheduleConfig, showEventUnits: boolean) {
    const calendarLevel = showEventUnits ? 2 : 1;
    const dataFieldIdsToShow = getEventBlockTypeByCalendarLevel(scheduleConfig.eventBlockTypes, calendarLevel)
        .dataFields.sort((a, b) => a.index - b.index)
        .map((ebtdf) => scheduleConfig.dataFields.find((df) => df.id === ebtdf.dataFieldId));
    return dataFieldIdsToShow;
}

export const getDataFieldValueByName = (item: EntityWithDataFieldValues, name: SportEventDataFieldNames) => item.dataFieldValues.find((dfv) => dfv.dataField.name === name);

export const getDataFieldValueById = (dataFieldValues: DataFieldValue[], dataFieldId) => dataFieldValues.find((dfv) => dfv.dataField.id === dataFieldId)?.value;

/**
 * A function that returns an event blocks' data field OR an inherited data field from either it's parent or schedule version (e.g. discipline is a schedule version data field)
 * @param bottomLevelEventBlock - The event block in question
 * @param dataFieldId - The id of the data field in question
 * @param eventBlockHierarchy - The entire event block hierarchy
 * @param scheduleVersionHierarchy - the schedule version hierarchy, used in case the data field is tied to a schedule version
 */
export const findInheritedDataFieldValue = (
    bottomLevelEventBlock: Hierarchy<EventBlock>,
    dataFieldId: string,
    eventBlockHierarchy: Hierarchy<EventBlock>,
    scheduleVersionHierarchy?: Hierarchy<ScheduleVersion>
): DataFieldListItem => {
    const getValueFromScheduleVersionHierarchy = (scheduleVersionId: string) => {
        const scheduleVersion = findHierarchyEntityById([scheduleVersionHierarchy], scheduleVersionId);
        if (scheduleVersion == null) {
            return null;
        }
        return scheduleVersion.item.dataFieldValues.find((dfv) => dfv.dataField.id === dataFieldId)?.value;
    };
    const getValueFromEventBlockHierarchy = (_eventBlock: string | Hierarchy<EventBlock>) => {
        let eventBlock: Hierarchy<EventBlock>;
        if (typeof _eventBlock === "string") {
            eventBlock = findHierarchyEntityById([eventBlockHierarchy], _eventBlock) as Hierarchy<EventBlock>;
        } else {
            eventBlock = _eventBlock;
        }
        const dataFieldValue = eventBlock.item.dataFieldValues.find((dfv) => dfv.dataField.id === dataFieldId);
        if (dataFieldValue != null) {
            // Data field value found
            return dataFieldValue.value;
        }
        if (eventBlock.item.parentId == null) {
            // No data field exists in the event block hierarchy, so we check the schedule version hierarchy
            if (scheduleVersionHierarchy == null) {
                return null;
            }
            return getValueFromScheduleVersionHierarchy(eventBlock.item.scheduleVersionId);
        }
        return getValueFromEventBlockHierarchy(eventBlock.item.parentId);
    };
    return getValueFromEventBlockHierarchy(bottomLevelEventBlock);
};

/**
 * Finds the next available venue location up the event tree. If top level event, it will return it's own venue location
 */
export const findInheritedVenue = (bottomLevelEventBlock: Hierarchy<EventBlock>, eventBlockHierarchy: Hierarchy<EventBlock>[]): VenueLocation => {
    const getParentVenue = (eventBlock: EventBlock) => {
        const parent = findHierarchyEntityById<EventBlock>(eventBlockHierarchy, eventBlock.parentId);
        return parent.item.venueLocation || getParentVenue(parent.item);
    };
    if (bottomLevelEventBlock.item.parentId == null) {
        // If no parent id we can assume it is a top level event block
        return bottomLevelEventBlock.item.venueLocation;
    }
    return getParentVenue(bottomLevelEventBlock.item);
};

/**
 * The function removes all items and its children from the hierarchy which match the event block type and where it's property matches the
 * value which is passed into this function
 * @param eventBlockHierarchyList - Array of Hierarchy<EventBlock> where items are going to be filtered
 * @param eventBlockTypeId - Id of the ScheduleConfigEventBlockType of the item, for example phase, event-unit etc.
 * @param property - a property of the item which we want to check the value against
 * @param value - a value which we want to check against, if this value is matched then the item is removed
 * @returns - filtered array of Hierarchy<EventBlock>
 */
export function filterEventBlockHierarchyOnValue<ValueType>(
    eventBlockHierarchyList: Hierarchy<EventBlock>[],
    eventBlockTypeId: string,
    property: keyof EventBlock,
    value: ValueType
): Hierarchy<EventBlock>[] {
    const newEventBlockHierarchyList: Hierarchy<EventBlock>[] = [];

    // Cycle through all of the event block hierarchy
    eventBlockHierarchyList.forEach((eventBlockHierarchy) => {
        // If the value is not present then we can add to the new array
        if (!isValuePresent<ValueType>(eventBlockHierarchy.item, eventBlockTypeId, property, value)) {
            newEventBlockHierarchyList.push({
                item: eventBlockHierarchy.item,
                // Call the filter function for the children of the item
                children: filterEventBlockHierarchyOnValue<ValueType>(eventBlockHierarchy.children, eventBlockTypeId, property, value),
            });
        }
    });

    return newEventBlockHierarchyList;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isValuePresent<ValueType extends any>(item: EventBlock, typeId: string, property: keyof EventBlock, value: ValueType): boolean {
    return item.type?.id === typeId && item[property] === value;
}

export const publishedColour = "#469CE2";
export const draftColour = "#DD9D36";

export function getScheduleVersionColour(scheduleVersion: ScheduleVersion): string {
    if (scheduleVersion) {
        return scheduleVersion.status === ScheduleStatus.Published ? publishedColour : draftColour;
    }
    return draftColour;
}

export function getScheduleStatusColour(scheduleStatus: ScheduleStatus): string {
    if (scheduleStatus) {
        return scheduleStatus === ScheduleStatus.Published ? publishedColour : draftColour;
    }
    return null;
}
// Returns true if both locations are null OR both locations have the same id
export const isEventAtVenue = (event: EventBlock, venue: VenueLocation) => {
    if (event.venueLocation == null || venue == null) {
        // eslint-disable-next-line eqeqeq
        return event.venueLocation == venue;
    }
    return event.venueLocation?.id === venue?.id;
};

// Returns all event units at the venueLocation
export const getEventsAtVenue = (eventUnits: EventBlock[], venueLocation: VenueLocation) => eventUnits.filter((eventUnit) => isEventAtVenue(eventUnit, venueLocation));

/**
 * Returns the given event block hierarchy with a filtered out data field value list to remove the null values
 */
export const removeNullDataFieldValuesFromEventBlock = (eventBlockHierarchy: Hierarchy<EventBlock>) => ({
    ...eventBlockHierarchy,
    item: { ...eventBlockHierarchy.item, dataFieldValues: eventBlockHierarchy.item.dataFieldValues.filter((dfv) => dfv.value != null) },
});

/**
 * Given a hierarchy the function will work it's way from the lowest level event block using it's parentId and work its way up the hierarchy find values
 * of the data field names
 * @param hierarchy - event block hierarchy where the parent id is present
 * @param initialParentId - parentId of the lowest level event block
 * @param dataFieldTypes - an array of EventBlockSystemDataFieldType which we want to find the value of eg) code, colour
 * @param getValue - a function to get the value from the data field value
 * @returns - set of key value pairs - { [dataFieldName] : value returned by getValue() }
 */
export function getEventBlockHierarchySystemDataFieldValues<GetFunctionParameterType, GetFunctionReturnType = GetFunctionParameterType>(
    hierarchy: Hierarchy<EventBlock>[],
    initialItem: EventBlock,
    dataFieldTypes: EventBlockSystemDataFieldType[],
    getValue: (value: GetFunctionParameterType) => GetFunctionReturnType
) {
    let { parentId } = initialItem;
    let dataFieldValues: { [key: string]: GetFunctionReturnType } = {};
    // For each dataFieldName get the value in the hierarchy for it with the id equal to the parent id
    dataFieldTypes.forEach((dataFieldName) => {
        const item = initialItem.type.dataFields.find((datafields) => datafields.systemDataFieldType === EventBlockSystemDataFieldType.Code);

        if (item == null) {
            // get the next event block
            const parent = findHierarchyEntityById(hierarchy, parentId).item;
            parentId = parent.id;
            const typeInfo = parent.type.dataFields.find((x) => x.systemDataFieldType === EventBlockSystemDataFieldType.Code);
            // if the value is present add to the object to be returned
            if (typeInfo != null) {
                const dataFieldValue = getDataFieldValueById(parent.dataFieldValues, typeInfo.dataFieldId);

                dataFieldValues = { ...dataFieldValues, [dataFieldName]: getValue(dataFieldValue) };
            }
        }
    });

    return dataFieldValues;
}

export const findTopCalendarlevelEventBlock = (currentEventBlock: Hierarchy<EventBlock>, eventBlockHierarchy: Hierarchy<EventBlock>[]): Hierarchy<EventBlock> => {
    if (currentEventBlock == null) {
        return null;
    }
    if (currentEventBlock.item.type.calendarLevel === 1) {
        return currentEventBlock;
    }
    return findTopCalendarlevelEventBlock(findHierarchyEntityById(eventBlockHierarchy, currentEventBlock.item.parentId), eventBlockHierarchy);
};

/* eslint-disable no-param-reassign */
/**
 * Checks for changes to the top level event block that might affect its children,
 * and adjusts the relevant properties on those children (sub events/scheduled items)
 * @param eventBlockHierarchy Event block hierarchy you wish to evaluate
 * @param existingEventBlock Previous state of the event block hierarchy you wish to evaluate
 */
export const checkForNecessarySubEventChanges = (eventBlockHierarchy: Hierarchy<EventBlock>, existingEventBlock: EventBlock) => {
    if (eventBlockHierarchy.children.length === 0) {
        return;
    }
    const venueChanged = eventBlockHierarchy.item.venueLocation.id !== existingEventBlock.venueLocation.id;
    const startTimeChanged = isDateTimeDifferent(eventBlockHierarchy.item.startAt, existingEventBlock.startAt);
    const endTimeChanged = isDateTimeDifferent(eventBlockHierarchy.item.endAt, existingEventBlock.endAt);
    if (venueChanged || startTimeChanged || endTimeChanged) {
        loopHierarchy<EventBlock>(eventBlockHierarchy, (childEventBlock) => {
            // Handle each change that affects child
            if (venueChanged) {
                childEventBlock.item.venueLocation = null;
            }
            if (startTimeChanged) {
                // Must evaluate both start and end times
                childEventBlock.item.startAt = evaluateDateTimeDifference(childEventBlock.item.startAt, eventBlockHierarchy.item.startAt, isBefore);
                childEventBlock.item.endAt = evaluateDateTimeDifference(childEventBlock.item.endAt, eventBlockHierarchy.item.startAt, isBefore);
            }
            if (endTimeChanged) {
                // Must evaluate both start and end times
                childEventBlock.item.startAt = evaluateDateTimeDifference(childEventBlock.item.startAt, eventBlockHierarchy.item.endAt, isAfter);
                childEventBlock.item.endAt = evaluateDateTimeDifference(childEventBlock.item.endAt, eventBlockHierarchy.item.endAt, isAfter);
            }
            if (compareAsc(childEventBlock.item.startAt, childEventBlock.item.endAt) === 0) {
                // If we reach here, it means neither the start nor the end time is within the new session time frame...
                if (isDateTimeDifferent(childEventBlock.item.startAt, eventBlockHierarchy.item.startAt)) {
                    // So if starts are equal, we know the session time frame has moved forward in time...
                    childEventBlock.item.startAt = evaluateDateTimeDifference(subMinutes(childEventBlock.item.startAt, 20), eventBlockHierarchy.item.startAt, isBefore); // Default scheduled item time
                } else {
                    // And if not, then it must have moved back in time.
                    childEventBlock.item.endAt = evaluateDateTimeDifference(addMinutes(childEventBlock.item.endAt, 20), eventBlockHierarchy.item.endAt, isAfter); // Default scheduled item time
                }
            }
        });
    }
    if (startTimeChanged || endTimeChanged) {
        translate(Content.schedule2.toasts.scheduled_items_start_end_times_affected);
        toast.info({
            title: translate(Content.common.alert),
            message: translate(Content.schedule2.toasts.scheduled_items_start_end_times_affected),
        });
    }
};
/* eslint-enable no-param-reassign */

const evaluateDateTimeDifference = (firstDate: Date, secondDate: Date, checker: (firstDate: Date, secondDate: Date) => boolean) => {
    if (checker(firstDate, secondDate)) {
        return secondDate;
    }
    return firstDate;
};

const isDateTimeDifferent = (firstDate: Date, secondDate: Date) => compareAsc(firstDate, secondDate) !== 0;

export const isFirstTimeStringBefore = (firstDateString: string, secondDateString: string) => {
    const firstDateInts = convertTimeStringToNum(firstDateString);
    const secondDateInts = convertTimeStringToNum(secondDateString);

    if (firstDateInts == null || secondDateInts == null) {
        return false;
    }

    if (firstDateInts[0] > secondDateInts[0]) {
        // If first time hours > second time hours
        return false;
    }

    if (firstDateInts[0] === secondDateInts[0]) {
        if (firstDateInts[1] > secondDateInts[1]) {
            // If first time minutes > second time minutes
            return false;
        }
        if (firstDateInts[1] === secondDateInts[1]) {
            if (firstDateInts[2] > secondDateInts[2]) {
                // If first time seconds > second time seconds
                return false;
            }
        }
    }

    return true;
};

export const splitTimeToSingle = (hours: number, minutes: number, seconds: number): string => {
    const date = new Date(2000, 0, 1, hours, minutes, seconds);
    const output = date.toLocaleTimeString(undefined, { hourCycle: "h23" });
    return output;
};

// Ensures there are hours, mins and seconds in the time string
export const isTimeStringValid = (time: string): boolean => time && time.split(":").length === 3;

// Given a hierarchy get all the event blocks out of it as a flat array
export const flattenEventBlockHierarchy = (eventBlockHierarchy: Hierarchy<EventBlock>, flattenEventBlocks: EventBlock[] = []) => {
    flattenEventBlocks.push(eventBlockHierarchy.item);
    eventBlockHierarchy.children.forEach((child) => flattenEventBlockHierarchy(child, flattenEventBlocks));
    return flattenEventBlocks;
};

export const SortEventBlocksByStartAt = (a: EventBlock, b: EventBlock) => (a.startAt != null && b.startAt != null ? a.startAt.valueOf() - b.startAt.valueOf() : 0);

export const isFilteredByDay = (filterType: FilterType) => [FilterType.DayVenue, FilterType.DayDataField, FilterType.VenueRunSheet].includes(filterType);

export const isFilterTypeCalendar = (filterType: FilterType) => filterType !== FilterType.VenueRunSheet && filterType !== FilterType.Summary;
